import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { createExpenseRouted, isLocalFirstMode, listLocalExpenses, listLocalCashPockets, listLocalBankAccounts } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { companyDb } from '@/offline';
import type { POSExpense } from '@/types';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import {
  Menu, Plus, X, Loader2, AlertTriangle, RefreshCw, RotateCcw, Trash2
} from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

const CATEGORIES = [
  "Tea",
  "Lunch",
  "Water",
  "Cleaning",
  "Stationery",
  "Local Transport",
  "Loading/Unloading",
  "Minor Repairs",
  "Utility Bills",
  "Rent",
  "Salaries",
  "Commission",
  "Fuel",
  "Maintenance",
  "Marketing",
  "Bank Charges",
  "Other"
];

export default function ExpensesPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const qc = useQueryClient();

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState<POSExpense | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<POSExpense | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  useEscapeToClose(() => setShowAddModal(false), showAddModal);
  useEscapeToClose(() => setShowCancelModal(null), Boolean(showCancelModal));
  useEscapeToClose(() => setShowDeleteModal(null), Boolean(showDeleteModal));

  // Form State
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'Cash' | 'Bank'>('Cash');
  const [selectedPocket, setSelectedPocket] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [remarks, setRemarks] = useState('');
  const [formError, setFormError] = useState('');

  // Filters State
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [filterStaff, setFilterStaff] = useState<string>('All');
  const dateRangeHook = useReportDateRange('This Month');

  // Fetch Expenses
  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ['posExpenses', useSyncStore.getState().posSyncMode],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        const rows = await listLocalExpenses();
        return rows.map((e) => ({
          name: e.serverName || e.clientOfflineId,
          posting_date: e.createdAt.slice(0, 10),
          amount: e.amount,
          category: e.category,
          payment_method: e.payment_method,
          cash_pocket: e.cash_pocket,
          bank_account: e.bank_account,
          remarks: e.remarks,
          status: e.status || 'Submitted',
          recorded_by: e.recorded_by,
          recorded_by_name: e.recorded_by_name,
        })) as POSExpense[];
      }
      const res = await posApi.getPOSExpenses();
      return res.data.message as POSExpense[];
    }
  });

  const uniqueStaff = useMemo(() => {
    const staffSet = new Set(expenses.map(e => e.recorded_by_name || e.recorded_by).filter(Boolean));
    return Array.from(staffSet) as string[];
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => {
      if (filterCategory !== 'All' && exp.category !== filterCategory) return false;
      if (filterStaff !== 'All' && (exp.recorded_by_name || exp.recorded_by) !== filterStaff) return false;
      if (exp.posting_date) {
        if (dateRangeHook.from_date && exp.posting_date < dateRangeHook.from_date) return false;
        if (dateRangeHook.to_date && exp.posting_date > dateRangeHook.to_date) return false;
      }
      return true;
    });
  }, [expenses, filterCategory, filterStaff, dateRangeHook.from_date, dateRangeHook.to_date]);

  // Fetch Cash Pockets
  const { data: pockets = [] } = useQuery({
    queryKey: ['cashPockets', useSyncStore((s) => s.posSyncMode), useSyncStore((s) => s.isServerReachable)],
    queryFn: async () => {
      if (isLocalFirstMode() || !navigator.onLine || !useSyncStore.getState().isServerReachable) {
        const local = await listLocalCashPockets();
        if (local.length) return local;
        const row = await companyDb().catalog_settings.get('cash_pockets');
        return (row?.value as any[]) || [];
      }
      try {
        const res = await posApi.getCashPockets();
        return res.data.message as any[];
      } catch {
        return listLocalCashPockets();
      }
    }
  });

  // Fetch Bank Accounts
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ['bankAccounts', useSyncStore((s) => s.posSyncMode), useSyncStore((s) => s.isServerReachable)],
    queryFn: async () => {
      if (isLocalFirstMode() || !navigator.onLine || !useSyncStore.getState().isServerReachable) {
        const local = await listLocalBankAccounts();
        if (local.length) return local;
        const row = await companyDb().catalog_settings.get('bank_accounts');
        return (row?.value as any[]) || [];
      }
      try {
        const res = await posApi.getBankAccounts();
        return res.data.message as any[];
      } catch {
        return listLocalBankAccounts();
      }
    }
  });

  // Create Expense Mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => createExpenseRouted(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posExpenses'] });
      qc.invalidateQueries({ queryKey: ['cashPockets'] });
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
      setShowAddModal(false);
      resetForm();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || 'Failed to record expense';
      setFormError(msg);
    }
  });

  // Cancel Expense Mutation
  const cancelMutation = useMutation({
    mutationFn: async (data: { name: string; cancellation_reason: string }) => {
      if (isLocalFirstMode() && (!navigator.onLine || !useSyncStore.getState().isServerReachable)) {
        throw new Error(
          'Cancel expense needs Online. Unsynced local expenses stay until Sync Now.',
        );
      }
      if (data.name.startsWith('exp_')) {
        throw new Error('This expense is still offline. Sync Now first, then cancel online.');
      }
      return posApi.cancelPOSExpense(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posExpenses'] });
      qc.invalidateQueries({ queryKey: ['cashPockets'] });
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
      setShowCancelModal(null);
      setCancelReason('');
      setCancelError('');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || 'Failed to cancel expense';
      setCancelError(msg);
    }
  });

  // Delete Expense Mutation
  const deleteMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      if (isLocalFirstMode() && (!navigator.onLine || !useSyncStore.getState().isServerReachable)) {
        throw new Error(
          'Delete expense needs Online. Unsynced local expenses stay until Sync Now.',
        );
      }
      if (data.name.startsWith('exp_')) {
        throw new Error('This expense is still offline. Sync Now first, then delete online.');
      }
      return posApi.deletePOSExpense(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posExpenses'] });
      qc.invalidateQueries({ queryKey: ['cashPockets'] });
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
      setShowDeleteModal(null);
      setDeleteError('');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || 'Failed to delete expense';
      setDeleteError(msg);
    }
  });

  const resetForm = () => {
    setCategory('');
    setAmount('');
    setPaymentMethod('Cash');
    setSelectedPocket('');
    setSelectedBank('');
    setRemarks('');
    setFormError('');
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setFormError('Amount must be greater than zero');
      return;
    }

    if (!category) {
      setFormError('Category is required');
      return;
    }

    const payload: any = {
      category,
      amount: amt,
      payment_method: paymentMethod,
      remarks,
    };

    if (paymentMethod === 'Cash') {
      if (selectedPocket) {
        payload.cash_pocket = selectedPocket;
      }
    } else {
      if (!selectedBank) {
        setFormError('Please select a Bank Account');
        return;
      }
      payload.bank_account = selectedBank;
    }

    createMutation.mutate(payload);
  };

  const handleCancelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCancelError('');

    if (!cancelReason.trim()) {
      setCancelError('Reason is required');
      return;
    }

    cancelMutation.mutate({
      name: showCancelModal!.name!,
      cancellation_reason: cancelReason.trim(),
    });
  };

  const handleDeleteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteError('');

    deleteMutation.mutate({
      name: showDeleteModal!.name!,
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto flex flex-col">
        <TrialBanner />
        <div className="p-6 flex-1 max-w-7xl mx-auto w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-150 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <PakRupeeIcon className="w-6 h-6 text-primary-600" /> {t('expenses.title')}
                </h1>
                <p className="text-gray-500 text-sm">{t('expenses.subtitle')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> {t('expenses.record')}
            </button>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col sm:flex-row gap-4 mb-6 items-end">
            <div className="flex-1 min-w-[200px] space-y-1.5">
              <label className="text-sm font-medium text-gray-700 block">{t('common.category')}</label>
              <select
                className="input-field"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="All">{t('expenses.allCategories')}</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px] space-y-1.5">
              <label className="text-sm font-medium text-gray-700 block">{t('expenses.staff')}</label>
              <select
                className="input-field"
                value={filterStaff}
                onChange={(e) => setFilterStaff(e.target.value)}
              >
                <option value="All">{t('expenses.allStaff')}</option>
                {uniqueStaff.map(staff => (
                  <option key={staff} value={staff}>{staff}</option>
                ))}
              </select>
            </div>
            <ReportDateRangeFilter
              dateRange={dateRangeHook.dateRange}
              onDateRangeChange={dateRangeHook.setDateRange}
              customFrom={dateRangeHook.customFrom}
              onCustomFromChange={dateRangeHook.setCustomFrom}
              customTo={dateRangeHook.customTo}
              onCustomToChange={dateRangeHook.setCustomTo}
              className="flex-[2]"
            />
          </div>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-red-50 text-red-650 rounded-xl">
                <PakRupeeIcon className="w-6 h-6" />
              </div>
              <div>
                <span className="text-sm text-gray-500 block">{t('expenses.totalRecorded')}</span>
                <span className="text-2xl font-bold text-gray-900 font-mono">
                  {formatMoney(filteredExpenses.filter(e => e.status === 'Submitted').reduce((acc, curr) => acc + curr.amount, 0))}
                </span>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-gray-50 text-gray-500 rounded-xl">
                <RefreshCw className="w-6 h-6 animate-pulse" />
              </div>
              <div>
                <span className="text-sm text-gray-500 block">{t('expenses.cancelledReversed')}</span>
                <span className="text-2xl font-bold text-gray-950 font-mono">
                  {formatMoney(filteredExpenses.filter(e => e.status === 'Cancelled').reduce((acc, curr) => acc + curr.amount, 0))}
                </span>
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="bg-white border border-gray-150 rounded-2xl shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="py-20 flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                <span className="text-sm text-gray-500">{t('expenses.loading')}</span>
              </div>
            ) : filteredExpenses.length === 0 ? (
              <div className="py-24 text-center">
                <PakRupeeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 text-lg">{t('expenses.emptyTitle')}</h3>
                <p className="text-gray-500 text-sm mt-1 max-w-sm mx-auto">
                  {t('expenses.emptyHelp')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="py-4 px-6">{t('common.date')}</th>
                      <th className="py-4 px-6">{t('common.category')}</th>
                      <th className="py-4 px-6">{t('expenses.paymentFrom')}</th>
                      <th className="py-4 px-6">{t('expenses.recordedBy')}</th>
                      <th className="py-4 px-6">{t('common.amount')}</th>
                      <th className="py-4 px-6">{t('common.status')}</th>
                      <th className="py-4 px-6 text-right">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {filteredExpenses.map((exp) => (
                      <tr key={exp.name} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-6">
                          <span className="font-medium text-gray-900 block">{exp.posting_date}</span>
                          <span className="text-xs text-gray-400 font-mono">{exp.name}</span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {exp.category}
                          </span>
                          {exp.remarks && (
                            <span className="block text-xs text-gray-400 mt-1 max-w-[200px] truncate" title={exp.remarks}>
                              {exp.remarks}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <span className="block text-gray-700 font-medium">
                            {exp.payment_method}
                          </span>
                          <span className="text-xs text-gray-400">
                            {exp.payment_method === 'Cash' ? exp.cash_pocket : exp.bank_account}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-500">
                          {exp.recorded_by_name || exp.recorded_by}
                          {exp.pos_shift && (
                            <span className="block text-xs text-primary-600 font-mono">Shift: {exp.pos_shift.split('-').pop()}</span>
                          )}
                        </td>
                        <td className="py-4 px-6 font-semibold font-mono text-gray-900">
                          {formatMoney(exp.amount)}
                        </td>
                        <td className="py-4 px-6">
                          {exp.status === 'Submitted' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-100">
                              {t('common.active')}
                            </span>
                          ) : (
                            <div>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-800 border border-red-100">
                                {t('common.cancelled')}
                              </span>
                              {exp.cancellation_reason && (
                                <span className="block text-xs text-red-400 mt-1 max-w-[150px] truncate" title={exp.cancellation_reason}>
                                  {t('expenses.reason')} {exp.cancellation_reason}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-6 text-right space-x-2">
                          {exp.status === 'Submitted' && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowCancelModal(exp);
                                setCancelReason('');
                                setCancelError('');
                              }}
                              className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded-lg inline-flex items-center gap-1.5 transition-all text-xs font-medium"
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> {t('expenses.reversal')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setShowDeleteModal(exp);
                              setDeleteError('');
                            }}
                            className="text-red-650 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg inline-flex items-center gap-1.5 transition-all text-xs font-medium"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Record Expense Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gray-50 shrink-0">
              <div>
                <h3 className="font-semibold text-gray-950 text-lg">{t('expenseModal.title')}</h3>
                <p className="text-xs text-gray-500">Record a shop or business expense paid from cash or bank</p>
              </div>
              <button type="button" onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Category</label>
                  <select
                    required
                    className="input-field"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">Select Category...</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ({getCurrencySymbol()})</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="input-field font-mono"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    required
                    className="input-field"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as any)}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank Account</option>
                  </select>
                </div>

                <div>
                  {paymentMethod === 'Cash' ? (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pay From Cash Drawer (Optional)</label>
                      <select
                        className="input-field"
                        value={selectedPocket}
                        onChange={(e) => setSelectedPocket(e.target.value)}
                      >
                        <option value="">Select Cash Drawer...</option>
                        {pockets.map(p => (
                          <option key={p.name} value={p.name}>{p.pocket_name} (Bal: {formatMoney(p.current_balance)})</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pay From Bank Account</label>
                      <select
                        required
                        className="input-field"
                        value={selectedBank}
                        onChange={(e) => setSelectedBank(e.target.value)}
                      >
                        <option value="">Select Bank Account...</option>
                        {bankAccounts.map(b => (
                          <option key={b.name} value={b.name}>{b.bank_name} - {b.account_title}</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks / Description</label>
                <textarea
                  className="input-field min-h-[90px]"
                  placeholder="Specify purpose of this payment (e.g. Electricity bill for June)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              {formError && (
                <div className="bg-red-50 text-red-650 p-3.5 rounded-xl text-sm flex items-start gap-2 border border-red-100">
                  <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
                  <span>{formError}</span>
                </div>
              )}
              </div>

              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-primary flex-1 py-2.5 flex items-center justify-center gap-1.5"
                >
                  {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Submit Payment
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary py-2.5"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancellation / Reversal Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-red-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-red-50">
              <h3 className="font-semibold text-red-950 text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" /> Reverse Expense Entry
              </h3>
              <button type="button" onClick={() => setShowCancelModal(null)} className="text-red-800 hover:text-red-950 p-1 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCancelSubmit} className="p-6 space-y-4">
              <p className="text-sm text-gray-600 leading-relaxed">
                You are about to cancel expense <strong className="font-mono text-red-650">{showCancelModal.name}</strong> of amount <strong className="font-semibold">{formatMoney(showCancelModal.amount)}</strong>.
                This action will credit back the balance to the source pocket/bank account.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('expenses.reversalReason')}</label>
                <textarea
                  required
                  className="input-field min-h-[90px] border-red-200 focus:ring-red-200"
                  placeholder="Provide brief reason (e.g. wrong entry amount recorded)"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>

              {cancelError && (
                <div className="bg-red-50 text-red-650 p-3 rounded-xl text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-red-500 mt-0.5" />
                  <span>{cancelError}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={cancelMutation.isPending}
                  className="bg-red-650 hover:bg-red-700 text-white font-medium px-4 py-2.5 rounded-xl flex-1 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  {cancelMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirm Reversal
                </button>
                <button
                  type="button"
                  onClick={() => setShowCancelModal(null)}
                  className="btn-secondary py-2.5"
                >
                  Keep Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-red-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-red-50">
              <h3 className="font-semibold text-red-950 text-lg flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" /> Delete Expense Entry
              </h3>
              <button type="button" onClick={() => setShowDeleteModal(null)} className="text-red-800 hover:text-red-950 p-1 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleDeleteSubmit} className="p-6 space-y-4">
              <p className="text-sm text-gray-600 leading-relaxed">
                You are about to permanently delete expense <strong className="font-mono text-red-650">{showDeleteModal.name}</strong> of amount <strong className="font-semibold">{formatMoney(showDeleteModal.amount)}</strong>.
                This action is irreversible and will {showDeleteModal.status !== 'Cancelled' ? 'credit back the balance to the source pocket/bank account and ' : ''}remove associated ledger entries.
              </p>

              {deleteError && (
                <div className="bg-red-50 text-red-650 p-3 rounded-xl text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-red-500 mt-0.5" />
                  <span>{deleteError}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={deleteMutation.isPending}
                  className="bg-red-650 hover:bg-red-700 text-white font-medium px-4 py-2.5 rounded-xl flex-1 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirm Deletion
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(null)}
                  className="btn-secondary py-2.5"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
